import { joinAllArgs, mapValues, memoize } from '@bugbug/core/utils/toolbox';
import { createSelector } from '@reduxjs/toolkit';

import type { TestChangeNotification } from './tests.types';
import type { RootState } from '../store';

import type { Grouping } from '@bugbug/core/types/groups';
import type { Step } from '@bugbug/core/types/steps';
import type { CurrentTestGroup, CurrentTestGrouping, Test } from '@bugbug/core/types/tests';

import { selectSingleProjectId } from '../project/project.selectors';
import { selectCurrentStepId } from '../steps/steps.selectors';
import { isTemporaryStep } from '../steps/steps.types';
import {
  selectTestRunGroup,
  selectTestRunGroups,
  selectTestRunStep,
  selectTestRunSteps,
} from '../testRun/testRun.selectors';

import { testsApi } from './tests.api';

const selectDomain = (state: RootState) => state.tests;

// Tests list
const selectTestsListDomain = createSelector(
  [selectSingleProjectId, (state: RootState) => state],
  (projectId, state) => testsApi.endpoints.getTests.select({ projectId })(state)?.data,
);

export const selectTests = createSelector(
  selectTestsListDomain,
  (tests) => tests ?? ([] as Test[]),
);

export const selectTest = memoize((testId) =>
  createSelector(selectTests, (tests) => tests.find((test) => test.id === testId)),
);

export const selectTestsIdsList = createSelector(selectTests, (tests) =>
  tests.map((test) => test.id),
);

export const selectHasUsedVariousScreenSizes = createSelector(selectTests, (tests) =>
  tests.some((test) => test.screenSizeType === 'mobile'),
);

// Current test
export const selectCurrentTestId = createSelector(selectDomain, (s) => s.currentTestId);

export const selectCurrentTestTestRunId = createSelector(selectDomain, (s) => s.currentTestRunId);

const selectCurrentTestDomain = createSelector(
  [selectCurrentTestId, selectCurrentTestTestRunId, (state: RootState) => state],
  (testId, testRunId, state) => {
    if (!testId) return undefined;
    return testsApi.endpoints.getCurrentTest.select({ id: testId, testRunId })(state)?.data;
  },
);

// Current test
export const selectCurrentTest = createSelector(selectCurrentTestDomain, (state) => state?.test);

export const selectCurrentTestIsRecording = createSelector(
  selectCurrentTest,
  (test) => test?.testRun?.status === 'recording',
);

export const selectCurrentTestIsPaused = createSelector(
  selectCurrentTest,
  (test) => test?.testRun?.status === 'paused',
);

export const selectCurrentTestProfileId = createSelector(
  selectCurrentTest,
  (test) => test?.runProfileId ?? null,
);

// Groups in current test
export const selectCurrentTestGroups = createSelector(
  selectCurrentTestDomain,
  (state) => state?.groups ?? {},
);

export const selectCurrentTestGroupsIds = createSelector(
  selectCurrentTest,
  (test) => test?.groups ?? [],
);

export const selectCurrentTestGroupsList = createSelector(
  selectCurrentTest,
  selectCurrentTestGroups,
  (test, groups) => test?.groups.map((id) => groups[id]),
);

export const selectCurrentTestGroup = memoize((groupId) =>
  createSelector(selectCurrentTestGroups, (groups) => groups[groupId]),
);

export const selectTestGroup = memoize((groupId) =>
  createSelector(selectCurrentTestGroups, (groups) => groups[groupId]),
);

export const selectGroup = memoize(
  (isTestRun, groupId) => (isTestRun ? selectTestRunGroup(groupId) : selectTestGroup(groupId)),
  joinAllArgs,
);

export const selectGroupStepsList = memoize(
  (groupId, isTestRun) =>
    createSelector(
      selectGroup(isTestRun, groupId),
      isTestRun ? selectTestRunSteps : selectCurrentTestSteps,
      (group, steps) =>
        // TODO: Remove this type casting after test run migration to TS
        (group as CurrentTestGroup).steps
          .map((stepId) => {
            if (!steps?.[stepId]) return null;
            return { ...steps[stepId], groupId };
          })
          .filter(Boolean),
    ),
  joinAllArgs,
);

export const selectGroups = memoize((isTestRun: boolean) =>
  createSelector(isTestRun ? selectTestRunGroups : selectCurrentTestGroups, (groups) => groups),
);

export const selectCurrentTestComponentsInUse = createSelector(
  selectCurrentTest,
  selectGroups(false),
  (test, groups) => test?.groups.filter((id) => groups[id].isComponent),
);

export const selectAllGroupsInstances = createSelector(selectGroups(false), (groups) =>
  Object.values(groups).reduce(
    (acc, group, _, list) => {
      acc[group.id] = list.filter(({ originId }) => originId === group.originId);
      return acc;
    },
    {} as Record<string, CurrentTestGrouping[]>,
  ),
);

export const selectAllGroupInstancesById = memoize((groupId?: Grouping['id']) =>
  createSelector(selectAllGroupsInstances, (groupsInstances) =>
    groupId ? groupsInstances[groupId] : [],
  ),
) as (groupId?: Grouping['id']) => (state: RootState) => CurrentTestGrouping[];

// Steps in current test
export const selectCurrentTestSteps = createSelector(
  selectCurrentTestDomain,
  (state) => state?.steps ?? {},
);

export const selectCurrentTestHasAnySavedStep = createSelector(selectCurrentTestSteps, (steps) =>
  Object.values(steps).some((step) => !isTemporaryStep(step)),
);

export const selectCurrentTestStepsActivation = createSelector(
  selectCurrentTestSteps,
  mapValues((step) => step.isActive),
);

export const selectCurrentTestStepsTemporaryIds = createSelector(selectCurrentTestSteps, (steps) =>
  Object.values(steps)
    .filter(isTemporaryStep)
    .map((step) => step.id),
);

export const selectCurrentTestStep = memoize(
  (stepId) => createSelector(selectCurrentTestSteps, (steps) => steps[stepId]),
  joinAllArgs,
);

export const selectCurrentTestSortedStepIds = createSelector(
  selectCurrentTest,
  selectCurrentTestGroups,
  (test, groupsMap) => {
    if (!test) return [];

    return test.groups.flatMap((groupId) => groupsMap[groupId].steps) ?? [];
  },
);

export const selectCurrentTestHasSteps = createSelector(
  selectCurrentTestSteps,
  selectCurrentTestGroups,
  (steps, groups) => !!Object.keys(steps).length && !!Object.keys(groups).length,
);

export const selectStep = memoize((stepId, isTestRun) => {
  const selectData = isTestRun ? selectTestRunStep : selectCurrentTestStep;
  return selectData(stepId);
}, joinAllArgs);

export const selectCurrentStepIndex = createSelector(
  selectCurrentTestSortedStepIds,
  selectCurrentStepId,
  (stepsIds, currentStepId) => {
    if (!currentStepId) return null;

    const index = stepsIds.indexOf(currentStepId);
    return index < 0 ? null : index;
  },
);

export const selectSortedSteps = createSelector(
  selectCurrentTest,
  selectCurrentTestGroups,
  selectCurrentTestSteps,
  (test, groups, steps) =>
    test
      ? test.groups.flatMap((groupId) =>
          // This groupId override is needed to make sure that the correct groupId is passed to the step
          groups[groupId].steps.map((stepId): Step => ({ ...steps[stepId], groupId })),
        )
      : [],
);

export const selectCurrentTestElementsScreenshots = createSelector(
  selectCurrentTestDomain,
  (state) => state?.elementsScreenshots ?? {},
);

export const selectCurrentTestElementScreenshot = memoize((id) =>
  createSelector(
    selectCurrentTestElementsScreenshots,
    (elementsScreenshots) => elementsScreenshots[id],
  ),
);

// Rest of the selectors
export const selectIsSingleTestLoading = createSelector(selectDomain, (s) => s.isSingleLoading);

export const selectTestChangeNotificationById = memoize((testId: string) =>
  createSelector(selectDomain, (state) => state.changeNotificationByTestId[testId]),
) as (testId: string) => (state: RootState) => TestChangeNotification | undefined;
